"use client";

import { useRef } from "react";
import styles from "./themetoggle.module.css";

/**
 * Blog theme toggle (client impl — import via ./ThemeToggle, not directly).
 *
 * Toggles data-blog-theme on the nearest [data-blog] ancestor and persists to
 * localStorage("amalbryx-blog-theme"). Dark is the default: light sets
 * data-blog-theme="light", dark REMOVES the attribute.
 *
 * The swap is wrapped in document.startViewTransition (feature-detected) with
 * an expanding clip-path circle from the button. The circle is driven by WAAPI
 * on ::view-transition-new(root); the default root cross-fade is neutralised
 * both by WAAPI overrides here and by the documented global CSS in blog.css
 * (see ThemeToggle.tsx docblock). prefers-reduced-motion or no support: an
 * instant swap.
 *
 * No React state: which icon shows is decided by CSS reacting to the ancestor
 * [data-blog-theme] attribute, so the pre-paint init script and this button
 * can never disagree (and there is no hydration mismatch).
 */

// Keep in sync with BLOG_THEME_STORAGE_KEY in ./ThemeToggle.tsx.
const STORAGE_KEY = "amalbryx-blog-theme";

type DocumentWithVT = Document & {
  startViewTransition?: (update: () => void) => {
    ready: Promise<void>;
    finished: Promise<void>;
  };
};

export function ThemeToggle({ className }: { className?: string }) {
  const btnRef = useRef<HTMLButtonElement>(null);

  const toggle = () => {
    const btn = btnRef.current;
    if (!btn) return;
    const root = btn.closest<HTMLElement>("[data-blog]");
    if (!root) return;

    const goingLight = root.getAttribute("data-blog-theme") !== "light";
    const apply = () => {
      if (goingLight) root.setAttribute("data-blog-theme", "light");
      else root.removeAttribute("data-blog-theme");
      try {
        window.localStorage.setItem(STORAGE_KEY, goingLight ? "light" : "dark");
      } catch {
        /* storage unavailable — theme still applies for this page */
      }
    };

    const doc = document as DocumentWithVT;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!doc.startViewTransition || reduce) {
      apply();
      return;
    }

    const rect = btn.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    const r = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y),
    );

    const vt = doc.startViewTransition(apply);
    vt.ready
      .then(() => {
        const duration = 380;
        // Freeze the outgoing snapshot (overrides the default fade-out).
        document.documentElement.animate(
          { opacity: [1, 1], mixBlendMode: ["normal", "normal"] },
          { duration, pseudoElement: "::view-transition-old(root)" },
        );
        // Expanding circle on the incoming snapshot.
        document.documentElement.animate(
          {
            clipPath: [
              `circle(0px at ${x}px ${y}px)`,
              `circle(${Math.ceil(r)}px at ${x}px ${y}px)`,
            ],
            opacity: [1, 1],
            mixBlendMode: ["normal", "normal"],
          },
          {
            duration,
            easing: "cubic-bezier(0.19, 1, 0.22, 1)",
            pseudoElement: "::view-transition-new(root)",
          },
        );
      })
      .catch(() => {
        /* transition skipped — the theme already applied */
      });
  };

  return (
    <button
      ref={btnRef}
      type="button"
      onClick={toggle}
      className={className ? `${styles.toggle} ${className}` : styles.toggle}
      aria-label="Toggle color theme"
      title="Toggle color theme"
    >
      {/* sun — shown in dark mode (click for light) */}
      <svg
        className={styles.iconSun}
        aria-hidden="true"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
      {/* moon — shown in light mode (click for dark) */}
      <svg
        className={styles.iconMoon}
        aria-hidden="true"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />
      </svg>
    </button>
  );
}

export default ThemeToggle;
